import React, { useEffect, useState } from "react";
import { Backdrop, Box, CircularProgress, CssBaseline, Grid } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import TableSearchBar from "../../../../../common/TableSearchBar";
import CommonTable from "../../../../../common/CommonTable";
import QuickTalesTableRow from "../../../../../common/QuickTalesTableRow";
import {
  drawerWidth,
  GeneralAdminRoutes,
  mainLayoutStyles,
  showNotification,
  AddQuicktalesToAreaTableHeadCells,
} from "../../../../../constants";
import { useAuth } from "../../../../../hooks";
import { quickTalesSearch } from "../../../../../modules/OrganizationAlbums/hooks/AlbumsHooks";
import { getAllGAQuickTales } from "../../../GAQuickTales/hooks/GAQuickTalesHooks";
import { getSingleUser } from "../../../../../modules/OrganizationQuickTales/hooks/QuickTalesHooks";
import { updateGroup } from "../../hooks/GAGroupsHooks";

const GAAddQuickTalesToGroupContainer = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [groupDetails, setGroupDetails] = useState(location?.state?.groupDetails);
  const [quickTalesChoices, setQuickTalesChoices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approvedQuickTales, setApprovedQuickTales] = useState([]);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const snap = await getAllGAQuickTales();
        const all = snap?.docs?.map((d) => ({ id: d.id, ...d.data() })) ?? [];
        const approvedRaw = all.filter((qt) => qt?.approvalStatus?.status === "approved");

        // Hydrate createdBy/narrator user data so Created By / Told By show usernames.
        const approved = await Promise.all(
          approvedRaw.map(async (qt) => {
            const next = { ...qt };
            try {
              if (next?.createdBy) {
                const u = await getSingleUser(next.createdBy);
                next.createdByData = u?.data() ? u.data() : null;
              }
            } catch (e) {
              // ignore
            }
            try {
              if (next?.narrator) {
                const n = await getSingleUser(next.narrator);
                next.narratorData = n?.data() ? n.data() : null;
              }
            } catch (e) {
              // ignore
            }
            return next;
          })
        );

        setApprovedQuickTales(approved);
      } catch (e) {
        console.log("error fetching GA quicktales:", e?.message);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  useEffect(() => {
    if (!location?.state?.groupDetails?.id) {
      navigate(GeneralAdminRoutes.groups, { replace: true });
      return;
    }
    setGroupDetails(location.state.groupDetails);

    const existing = location.state.groupDetails?.quicktales || [];
    const filtered = (approvedQuickTales || []).filter((qt) => !existing.includes(qt.id));
    setQuickTalesChoices(filtered);
  }, [location.state, approvedQuickTales]);

  const handleSearchQuickTales = async (e) => {
    const value = e.target.value;
    if (value.length >= 1) {
      const temp = await quickTalesSearch(approvedQuickTales, value);
      setQuickTalesChoices(temp);
    } else {
      const existing = groupDetails?.quicktales || [];
      setQuickTalesChoices((approvedQuickTales || []).filter((qt) => !existing.includes(qt.id)));
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const existing = groupDetails?.quicktales || [];
      const updated = [...existing, ...selected];
      const unique = [...new Set(updated)];

      await updateGroup(groupDetails.id, {
        quicktales: unique,
        updatedAt: new Date(),
        updatedBy: currentUser?.id,
      });

      setSelected([]);
      showNotification("Group quicktales added successfully.");
      navigate(
        GeneralAdminRoutes.groupDetails,
        {
          replace: true,
          state: {
            groupDetails: { ...groupDetails, quicktales: unique },
            activeTab: "quicktales",
          },
        }
      );
    } catch (error) {
      console.log("error updating group quicktales:", error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", width: "100%", paddingRight: 4 }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={`Add QuickTales to '${groupDetails?.name || ""}'`}
        bgColor="#fff"
        selected={selected}
        setSelected={setSelected}
        editEnabled={true}
        cancelButton={true}
        loading={loading}
        submitButtonText={"Submit"}
        onCancelClick={() => {
          navigate(
            GeneralAdminRoutes.groupDetails,
            { replace: true, state: { groupDetails, activeTab: "quicktales" } }
          );
        }}
        handleSave={handleSave}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          <TableSearchBar placeholder={"Search"} handleSearch={handleSearchQuickTales} />
          <CommonTable
            tableData={quickTalesChoices}
            selected={selected}
            setSelected={setSelected}
            CommonTableRow={QuickTalesTableRow}
            showPosition={false}
            tableHeadData={AddQuicktalesToAreaTableHeadCells}
            initialOrder="desc"
            initialOrderBy="approvalStatus.updatedAt"
          />
        </Grid>
      </Grid>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
        onClick={() => setLoading(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default GAAddQuickTalesToGroupContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};
